import React, { useEffect } from "react";
import { SignUp } from "@clerk/clerk-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";

const SignUpPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isSignedIn } = useUser();
  const redirectTo = searchParams.get("redirectTo") || "/";

  // If user is already signed in, redirect to dashboard or intended destination
  useEffect(() => {
    if (isSignedIn) {
      const safeRedirectPath = validateRedirectPath(redirectTo);
      navigate(safeRedirectPath, { replace: true });
    }
  }, [isSignedIn, navigate, redirectTo]);

  // Validate redirect path to ensure it's safe
  const validateRedirectPath = (path) => {
    if (!path || !path.startsWith("/")) return "/";
    try {
      new URL(path, window.location.origin);
      return path;
    } catch {
      return "/";
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Black background with marketing content */}
      <div className="hidden lg:flex lg:w-1/2 bg-black relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-gray-800/20 rounded-full blur-3xl"></div>
        </div>

        <div className="absolute top-8 left-8 z-20">
          <a
            href={process.env.REACT_APP_LANDING_URL || "https://uttik-landing-page-prod-325801373008.us-central1.run.app"}
            aria-label="Go to Uttik home"
            className="inline-flex items-center space-x-3 px-3 py-2 rounded cursor-pointer"
            style={{ textDecoration: "none" }}
          >
            <img
              src="/logo.svg"
              alt="Uttik"
              className="h-14 w-14 object-contain rounded pointer-events-none"
            />
            <span className="text-white text-3xl font-bold">Uttik</span>
          </a>
        </div>

        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="max-w-lg">
            <h1 className="text-5xl font-bold leading-tight mb-6">
              The Answer Engine
              <br />
              <span className="text-gray-400">
                That Builds Trust
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Instant, trusted answers that guide buying decisions, reduce
              support load, and grow lifetime customer value.
            </p>
            <div className="flex flex-col space-y-3 text-gray-400">
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Smarter than SEO</span>
              </div>
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Quicker than chatbots</span>
              </div>
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Designed to turn search into sales</span>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-8 text-white/70 text-sm">
          © 2025 Uttik. All rights reserved.
        </div>
      </div>

      {/* Right side - Sign up form */}
      <div className="w-full lg:w-1/2 flex flex-col">
        <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-4">
          <div className="flex items-center space-x-3">
            <img
              src="/logo.svg"
              alt="Uttik"
              className="h-8 w-8 object-contain rounded"
            />
            <div>
              <h1 className="text-lg font-bold text-gray-900">Uttik</h1>
              <p className="text-xs text-gray-500 font-medium">
                AI Answer Engine
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-md w-full space-y-8">
            <div>
              <SignUp
                redirectUrl={
                  window.location.origin +
                  (redirectTo !== "/" ? redirectTo : "/")
                }
                signInUrl="/sign-in"
                appearance={{
                  elements: {
                    formButtonPrimary: "your-custom-button-class",
                    card: "your-custom-card-class",
                    // Add other element classes to reduce spacing
                  },
                  variables: {
                    spacingUnit: "0.7rem", // Reduce base spacing unit
                  },
                }}
              />
            </div>

            <div className="text-center text-sm text-gray-500">
              <p>
                Need help?{" "}
                <a
                  href="mailto:support@uttik.com"
                  className="text-gray-600 hover:text-black font-medium transition-colors underline"
                >
                  Contact support
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
